import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@/pages/strategic/hooks/useAutoSaveParams';

/**
 * Custom hook for Board Governance New auto-save functionality
 * 
 * Manages auto-save detection and restoration for Board Governance Overview with:
 * - Polling for modified meetings every 500ms during edit mode
 * - Auto-save triggered when meetings are modified
 * - Restore prompt when user enters edit mode with pending auto-save
 * 
 * @param isEditing - Whether the component is in edit mode
 * @param modifiedMeetings - Record of modified meetings
 * @param setModifiedMeetings - Function to update modified meetings
 */
export const useBoardGovernanceAutoSave = (
    isEditing: boolean,
    modifiedMeetings: Record<string, any>,
    setModifiedMeetings: (meetings: Record<string, any>) => void,
    autoSaveId?: string
) => {
    // Auto-save state
    const [autoSaveVisible, setAutoSaveVisible] = useState(false);
    
    const basePageKey = EDIT_PAGE_KEY_MAP.studio_board_governance;
    
    const boardGovernancePageKey = useMemo(() => {
        if (autoSaveId) {
            return `${basePageKey}_${autoSaveId}`;
        }
        return basePageKey;
    }, [basePageKey, autoSaveId]);

    // Auto-save: get methods (check if auto-save exists)
    const autoSaveMethods: any = useAutoSaveParams({
        pageKey: boardGovernancePageKey,
    });

    const {
        getSaveParamsValue,
        showRestorePrompt,
        deleteSaveParamsByPageKey
    } = autoSaveMethods;

    // Current save params for auto-save comparison
    const currentSaveParams = useMemo(() => {
        if (!isEditing) {
            // When not editing, return empty params to prevent auto-save from triggering
            return {
                modifiedMeetings: {},
            };
        }

        return {
            modifiedMeetings: modifiedMeetings,
        };
    }, [isEditing, modifiedMeetings]);

    // Memoize initialParams to capture the starting state when entering edit mode
    const initialParamsRef = useRef<{
        modifiedMeetings: Record<string, any>;
    }>({
        modifiedMeetings: {},
    });

    // Capture initialParams when entering edit mode
    useEffect(() => {
        if (isEditing) {
            // Capture the initial state when entering edit mode
            // This should be empty initially (no modifications yet)
            if (Object.keys(initialParamsRef.current.modifiedMeetings).length === 0) {
                initialParamsRef.current = {
                    modifiedMeetings: JSON.parse(JSON.stringify(modifiedMeetings)),
                };
                // console.log('🎯 [BOARD GOVERNANCE AUTO-SAVE] Captured initial modified meetings:', initialParamsRef.current);
            }
        } else if (!isEditing) {
            // Reset initialParams when exiting edit mode
            initialParamsRef.current = {
                modifiedMeetings: {},
            };
        }
    }, [isEditing, modifiedMeetings]);

    // Use auto-save hook
    useAutoSaveParams({
        pageKey: boardGovernancePageKey,
        initialParams: initialParamsRef.current,
        saveParamsCur: currentSaveParams,
        isBegin: isEditing,
        isChangeValue: true,
    });

    // Calculate hasUnsavedChanges for RoutePrompt
    const hasUnsavedChanges = useMemo(() => {
        if (!isEditing) return false;
        
        // Check if there are any modified meetings
        const hasModifications = Object.keys(modifiedMeetings).length > 0;
        
        // console.log('🔍 [BOARD GOVERNANCE] hasUnsavedChanges check:', {
        //     isEditing,
        //     hasModifications,
        //     modifiedMeetingsCount: Object.keys(modifiedMeetings).length,
        // });
        
        return hasModifications;
    }, [isEditing, modifiedMeetings]);

    // Handler: Check for auto-save before entering edit mode
    const handleBoardGovernanceEdit = useCallback((originalEditHandler: () => void) => {
        if (showRestorePrompt(boardGovernancePageKey)) {
            setAutoSaveVisible(true);
        } else {
            originalEditHandler();
        }
    }, [showRestorePrompt, boardGovernancePageKey]);

    // Handler: Discard auto-save and enter edit mode
    const handleAutoSaveCancel = useCallback((originalEditHandler: () => void) => {
        setAutoSaveVisible(false);
        if (boardGovernancePageKey) {
            deleteSaveParamsByPageKey(boardGovernancePageKey);
        }
        originalEditHandler();
    }, [deleteSaveParamsByPageKey, boardGovernancePageKey]);

    // Handler: Restore from auto-save
    const handleAutoSaveConfirm = useCallback((originalEditHandler: () => void) => {
        const saveValue = getSaveParamsValue(boardGovernancePageKey) || {};

        // console.log('🔄 [BOARD GOVERNANCE AUTO-SAVE RESTORE] Restoring from auto-save:', saveValue);

        setAutoSaveVisible(false);

        // Update modifiedMeetings with data from auto-save
        if (saveValue.modifiedMeetings && typeof saveValue.modifiedMeetings === 'object') {
            setModifiedMeetings(saveValue.modifiedMeetings);
        }

        // Enter edit mode after state updates
        setTimeout(() => {
            originalEditHandler();
        }, 100);
    }, [getSaveParamsValue, boardGovernancePageKey, setModifiedMeetings]);

    // Clear auto-save after successful save
    const clearBoardGovernanceAutoSave = useCallback(() => {
        if (boardGovernancePageKey) {
            deleteSaveParamsByPageKey(boardGovernancePageKey);
        }
    }, [deleteSaveParamsByPageKey, boardGovernancePageKey]);

    return {
        // State
        autoSaveVisible,
        boardGovernancePageKey,

        // Handlers
        handleBoardGovernanceEdit,
        handleAutoSaveCancel,
        handleAutoSaveConfirm,
        clearBoardGovernanceAutoSave,

        // Comparison result for RoutePrompt
        hasUnsavedChanges,
    };
};
